import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Search,
  Loader2,
  Lock,
  Sparkles,
  TrendingUp,
  Home,
  Building,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  X,
  Scale,
} from "lucide-react";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const money = (v) =>
  typeof v === "number" ? `$${Math.round(v).toLocaleString()}` : "—";
const pct = (v, d = 1) =>
  typeof v === "number" ? `${(v * 100).toFixed(d)}%` : "—";
const num = (v) => (typeof v === "number" ? v.toLocaleString() : "—");

const GrowthCell = ({ value }) => {
  const pos = typeof value === "number" && value > 0;
  const neg = typeof value === "number" && value < 0;
  return (
    <td
      className={`px-3 py-2 text-right tabular-nums ${
        pos ? "text-emerald-600" : neg ? "text-red-500" : "text-slate-500"
      }`}
    >
      {pct(value)}
    </td>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testId}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-lg font-semibold text-slate-900 mt-0.5">{value}</p>
  </div>
);

// Metric rows for suburb comparison. better: 'high' | 'low' | null (neutral)
const COMPARE_ROWS = [
  { label: "Typical price", get: (m) => m.summary?.typical_price, fmt: money, better: null },
  { label: "Weekly rent", get: (m) => m.summary?.rent, fmt: money, better: null },
  { label: "Gross yield", get: (m) => m.summary?.gross_yield, fmt: (v) => pct(v, 2), better: "high" },
  { label: "Price growth (1yr)", get: (m) => m.growth_annualised?.price_1y_growth_annualised, fmt: (v) => pct(v), better: "high" },
  { label: "Price growth (5yr p.a.)", get: (m) => m.growth_annualised?.price_5y_growth_annualised, fmt: (v) => pct(v), better: "high" },
  { label: "Rent growth (5yr p.a.)", get: (m) => m.growth_annualised?.rent_5y_growth_annualised, fmt: (v) => pct(v), better: "high" },
  { label: "Vacancy rate", get: (m) => m.demand?.vacancy_rate, fmt: (v) => pct(v, 2), better: "low" },
  { label: "Days on market", get: (m) => m.demand?.dom, fmt: (v) => (typeof v === "number" ? `${Math.round(v)}d` : "—"), better: "low" },
  { label: "Inventory (months)", get: (m) => m.supply?.inventory, fmt: (v) => (typeof v === "number" ? v.toFixed(1) : "—"), better: "low" },
];

export default function SuburbResearcher() {
  const [hasAccess, setHasAccess] = useState(null);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [propertyType, setPropertyType] = useState("house");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [shortlist, setShortlist] = useState([]);
  const [savingShortlist, setSavingShortlist] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    api
      .get("/subscription/status")
      .then((res) => setHasAccess(!!res.data?.has_access))
      .catch(() => setHasAccess(false));
  }, []);

  const fetchShortlist = () => {
    api
      .get("/suburb-research/shortlist")
      .then((res) => {
        const list = res.data.shortlist || [];
        setShortlist(list);
        setCompareA((prev) => (list.some((s) => s.id === prev) ? prev : (list[0]?.id || "")));
        setCompareB((prev) => (list.some((s) => s.id === prev) ? prev : (list[1]?.id || "")));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (hasAccess) fetchShortlist();
  }, [hasAccess]);

  const isSaved =
    report &&
    shortlist.some(
      (s) =>
        s.loc_pid === report.locality?.loc_pid &&
        s.property_type === report.property_type
    );

  const saveToShortlist = async () => {
    if (!report) return;
    setSavingShortlist(true);
    try {
      await api.post("/suburb-research/shortlist", {
        locality: report.locality,
        property_type: report.property_type,
        metrics: report.metrics,
        ai_summary: report.ai_summary,
      });
      fetchShortlist();
      toast.success(`${report.locality?.locality} saved to your shortlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save to shortlist");
    } finally {
      setSavingShortlist(false);
    }
  };

  const openFromShortlist = (item) => {
    setReport(item.report);
    setSelectedLoc(null);
    setShowSuggestions(false);
    setSuggestions([]);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeShortlist = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/suburb-research/shortlist/${id}`);
      setShortlist((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      toast.error("Could not remove from shortlist");
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2 || selectedLoc) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { q };
        if (stateFilter) params.state = stateFilter;
        const res = await api.get("/suburb-research/localities", { params });
        setSuggestions(res.data.results || []);
        setShowSuggestions(true);
      } catch (e) {
        // silent — autocomplete is best-effort
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, stateFilter, hasAccess]);

  const pickSuggestion = (s) => {
    setSelectedLoc(s);
    setQuery(`${s.locality}, ${s.state_name} ${s.postcode}`);
    setStateFilter(s.state_name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const runResearch = async (loc) => {
    const target = loc || selectedLoc;
    setLoading(true);
    setReport(null);
    setShowSuggestions(false);
    setSuggestions([]);
    try {
      const body = { property_type: propertyType };
      if (target?.loc_pid) {
        body.loc_pid = target.loc_pid;
        body.suburb = target.locality;
        body.state = target.state_name;
      } else {
        body.suburb = query.split(",")[0].trim();
        if (stateFilter) body.state = stateFilter;
      }
      if (!body.suburb && !body.loc_pid) {
        toast.error("Please enter a suburb");
        setLoading(false);
        return;
      }
      const res = await api.post("/suburb-research", body);
      setReport(res.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 402) {
        setHasAccess(false);
      } else {
        toast.error(error.response?.data?.detail || "Could not research this suburb. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const Header = (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-[#547d91]/10 flex items-center justify-center">
        <MapPin className="w-6 h-6 text-[#547d91]" />
      </div>
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">AI Suburb Researcher</h1>
        <p className="text-slate-500 text-sm">
          Institutional-grade suburb intelligence — growth, supply &amp; demand, with an AI verdict.
        </p>
      </div>
    </div>
  );

  if (hasAccess === null) {
    return (
      <div className="space-y-6" data-testid="suburb-researcher">
        {Header}
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            <p>Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="suburb-researcher">
        {Header}
        <Card className="overflow-hidden" data-testid="suburb-locked-card">
          <div className="bg-[#547d91] px-6 py-5 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold">Part of your AI Buyers Agent plan</h2>
              <p className="text-white/80 text-sm">
                AI Suburb Researcher is included in the same subscription — start there to unlock it.
              </p>
            </div>
          </div>
          <CardContent className="p-6 space-y-5">
            <p className="text-sm text-slate-600">
              Go beyond a gut feel. The AI Suburb Researcher assesses over <span className="font-semibold text-slate-800">100,000 data points nationwide</span>, pulling enhanced-tier market data for any Australian suburb and turning it into a clear read on where to buy, hold or hold off — so you can shortlist with confidence and back every decision with numbers.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-700">
              <div className="flex items-start gap-2.5">
                <Home className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Key market snapshot</span> — typical price, weekly rent, gross yield &amp; annual sales volume</span>
              </div>
              <div className="flex items-start gap-2.5">
                <TrendingUp className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Enhanced growth history</span> — 1, 3, 5 &amp; 10-year price and rent growth (annualised + cumulative)</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Building className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Live supply &amp; demand</span> — vacancy rate, days on market, vendor discounting &amp; auction clearance</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Search className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Buyer &amp; renter demand</span> — search-interest indexes, inventory, stock on market, building approvals &amp; typical hold period</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">AI Suburb Verdict</span> — a plain-English buy / hold / caution call for every suburb you research</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Scale className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Compare suburbs side-by-side</span> to quickly weigh growth, yield and demand</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Bookmark className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Save a shortlist</span> of suburbs and revisit them anytime</span>
              </div>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#547d91] mt-0.5 shrink-0" />
                <span><span className="font-medium">Every Australian suburb</span> covered — search by name or locality</span>
              </div>
            </div>
            <Link to="/property-researcher">
              <Button className="bg-[#547d91] hover:bg-[#456a7a] rounded-full" data-testid="suburb-unlock-btn">
                Unlock with AI Buyers Agent <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const m = report?.metrics || {};
  const summary = m.summary || {};
  const ann = m.growth_annualised || {};
  const cum = m.growth_cumulative || {};
  const demand = m.demand || {};
  const supply = m.supply || {};

  return (
    <div className="space-y-6" data-testid="suburb-researcher">
      {Header}

      {/* Search bar */}
      <Card>
        <CardContent className="py-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px_auto] gap-3 items-end">
            <div className="relative">
              <Label htmlFor="suburb-search" className="mb-1.5 block">Suburb</Label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="suburb-search"
                  data-testid="suburb-search-input"
                  className="pl-9"
                  placeholder="e.g. Carina, Richmond, Bondi…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedLoc(null);
                  }}
                  onFocus={() => suggestions.length && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onKeyDown={(e) => { if (e.key === "Enter") runResearch(); }}
                  autoComplete="off"
                />
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto" data-testid="suburb-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.loc_pid}
                      type="button"
                      onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                      data-testid={`suburb-suggestion-${s.loc_pid}`}
                    >
                      <span className="text-slate-800">{s.locality}</span>
                      <span className="text-xs text-slate-400">{s.state_name} {s.postcode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="mb-1.5 block">State</Label>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger data-testid="suburb-state-select"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  {AU_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <div className="inline-flex rounded-lg border border-slate-200 p-1">
                <button
                  type="button"
                  onClick={() => setPropertyType("house")}
                  className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${propertyType === "house" ? "bg-[#547d91] text-white" : "text-slate-600"}`}
                  data-testid="suburb-proptype-house"
                >
                  <Home className="w-4 h-4" /> House
                </button>
                <button
                  type="button"
                  onClick={() => setPropertyType("unit")}
                  className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${propertyType === "unit" ? "bg-[#547d91] text-white" : "text-slate-600"}`}
                  data-testid="suburb-proptype-unit"
                >
                  <Building className="w-4 h-4" /> Unit
                </button>
              </div>
              <Button
                onClick={() => runResearch()}
                disabled={loading}
                className="bg-[#547d91] hover:bg-[#456a7a] rounded-full px-6"
                data-testid="suburb-research-btn"
              >
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Researching…</>) : (<><Search className="w-4 h-4 mr-2" /> Research</>)}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">Tip: pick a suburb from the dropdown for the most accurate match. Include the state if the name is common.</p>
        </CardContent>
      </Card>

      {/* Shortlist */}
      {shortlist.length > 0 && (
        <Card data-testid="suburb-shortlist">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <Bookmark className="w-4 h-4 text-[#547d91]" />
              <span className="text-sm font-medium text-slate-700">Your shortlist</span>
              <span className="text-xs text-slate-400">· tap to reopen (free)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {shortlist.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openFromShortlist(item)}
                  className="group flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:border-[#547d91] hover:bg-[#547d91]/5 pl-3 pr-2 py-1.5 transition-colors"
                  data-testid={`shortlist-item-${item.id}`}
                >
                  <span className="text-sm text-slate-700">{item.locality}</span>
                  <span className="text-[10px] text-slate-400">{item.state_name} · {item.property_type}</span>
                  <span
                    onClick={(e) => removeShortlist(e, item.id)}
                    className="w-5 h-5 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50"
                    data-testid={`shortlist-remove-${item.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}



      {/* Compare suburbs */}
      {shortlist.length >= 2 && (() => {
        const itemA = shortlist.find((s) => s.id === compareA);
        const itemB = shortlist.find((s) => s.id === compareB);
        const mA = itemA?.report?.metrics || {};
        const mB = itemB?.report?.metrics || {};
        let winsA = 0, winsB = 0;
        const rows = COMPARE_ROWS.map((r) => {
          const a = r.get(mA);
          const b = r.get(mB);
          let winner = null;
          if (r.better && typeof a === "number" && typeof b === "number" && a !== b) {
            const aWins = r.better === "high" ? a > b : a < b;
            winner = aWins ? "a" : "b";
            if (aWins) winsA++; else winsB++;
          }
          return { ...r, a, b, winner };
        });
        const lead = winsA === winsB ? null : winsA > winsB ? itemA : itemB;
        return (
          <Card data-testid="suburb-compare">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-lg">
                <Scale className="w-5 h-5 text-[#547d91]" /> Compare Suburbs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Select value={compareA} onValueChange={setCompareA}>
                  <SelectTrigger data-testid="compare-a-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {shortlist.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.locality} · {s.state_name} ({s.property_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={compareB} onValueChange={setCompareB}>
                  <SelectTrigger data-testid="compare-b-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {shortlist.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.locality} · {s.state_name} ({s.property_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {itemA && itemB && itemA.id === itemB.id ? (
                <p className="text-sm text-slate-500 text-center py-4">Pick two different suburbs to compare.</p>
              ) : itemA && itemB ? (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-500">
                        <th className="px-3 py-2 text-left font-medium"></th>
                        <th className="px-3 py-2 text-right font-medium">{itemA.locality}</th>
                        <th className="px-3 py-2 text-right font-medium">{itemB.locality}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.label} className="border-b border-slate-50">
                          <td className="px-3 py-2 text-slate-600">{r.label}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.winner === "a" ? "text-emerald-600" : "text-slate-800"}`}>
                            {r.fmt(r.a)}{r.winner === "a" && <span className="ml-1 text-emerald-500">▲</span>}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.winner === "b" ? "text-emerald-600" : "text-slate-800"}`}>
                            {r.fmt(r.b)}{r.winner === "b" && <span className="ml-1 text-emerald-500">▲</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 rounded-lg bg-[#547d91]/5 border border-[#547d91]/20 px-4 py-3 text-sm text-slate-700 text-center" data-testid="compare-verdict">
                    {lead ? (
                      <>On the numbers, <span className="font-semibold text-[#547d91]">{lead.locality}</span> looks the stronger buy — winning {Math.max(winsA, winsB)} of {winsA + winsB} scored metrics.</>
                    ) : (
                      <>It's line-ball — both suburbs score evenly across the metrics.</>
                    )}
                    <span className="block text-xs text-slate-400 mt-1">Scored on yield, growth, vacancy, days on market &amp; inventory. Price is shown for context only.</span>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        );
      })()}

      {loading && !report && (
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            <p>Pulling market data and writing your suburb verdict…</p>
          </CardContent>
        </Card>
      )}

      {report && (
        <div className="space-y-6 animate-fade-in" data-testid="suburb-report">
          {/* Headline */}
          <Card>
            <CardContent className="py-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900" data-testid="suburb-report-name">
                      {report.locality?.locality}
                    </h2>
                    <Badge variant="secondary">{report.locality?.state_name} {report.locality?.postcode}</Badge>
                    <Badge className="bg-[#547d91]/10 text-[#547d91] hover:bg-[#547d91]/10 capitalize">{report.property_type}</Badge>
                  </div>
                  {summary.confidence && (
                    <p className="text-xs text-slate-400 mt-1">Data confidence: {summary.confidence}</p>
                  )}
                </div>
                <Button
                  variant={isSaved ? "secondary" : "outline"}
                  onClick={saveToShortlist}
                  disabled={savingShortlist || isSaved}
                  className="rounded-full shrink-0"
                  data-testid="suburb-save-shortlist-btn"
                >
                  {isSaved ? (
                    <><BookmarkCheck className="w-4 h-4 mr-2 text-[#547d91]" /> Saved</>
                  ) : savingShortlist ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Bookmark className="w-4 h-4 mr-2" /> Save to shortlist</>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <Stat label="Typical price" value={money(summary.typical_price)} testId="stat-price" />
                <Stat label="Weekly rent" value={money(summary.rent)} testId="stat-rent" />
                <Stat label="Gross yield" value={pct(summary.gross_yield, 2)} testId="stat-yield" />
                <Stat label="Annual sales" value={num(summary.annual_sales_volume)} testId="stat-sales" />
              </div>
            </CardContent>
          </Card>

          {/* AI Verdict */}
          {report.ai_summary && (
            <Card className="border-[#547d91]/30">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-[#547d91]" /> AI Suburb Verdict
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="advisor-md text-slate-800" data-testid="suburb-ai-summary">
                  <ReactMarkdown>{report.ai_summary}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Growth */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-[#547d91]" /> Growth Rates
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs border-b border-slate-100">
                    <th className="px-3 py-2 text-left font-medium">Annualised</th>
                    <th className="px-3 py-2 text-right font-medium">1 yr</th>
                    <th className="px-3 py-2 text-right font-medium">3 yr</th>
                    <th className="px-3 py-2 text-right font-medium">5 yr</th>
                    <th className="px-3 py-2 text-right font-medium">10 yr</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-50">
                    <td className="px-3 py-2 text-slate-600">Price</td>
                    <GrowthCell value={ann.price_1y_growth_annualised} />
                    <GrowthCell value={ann.price_3y_growth_annualised} />
                    <GrowthCell value={ann.price_5y_growth_annualised} />
                    <GrowthCell value={ann.price_10y_growth_annualised} />
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Rent</td>
                    <GrowthCell value={ann.rent_1y_growth_annualised} />
                    <GrowthCell value={ann.rent_3y_growth_annualised} />
                    <GrowthCell value={ann.rent_5y_growth_annualised} />
                    <GrowthCell value={ann.rent_10y_growth_annualised} />
                  </tr>
                </tbody>
              </table>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Stat label="Price growth (1yr)" value={pct(cum.price_1y_growth)} />
                <Stat label="Price growth (5yr cml)" value={pct(cum.price_5y_growth)} />
                <Stat label="Price growth (10yr cml)" value={pct(cum.price_10y_growth)} />
              </div>
            </CardContent>
          </Card>

          {/* Supply & Demand */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Supply &amp; Demand</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Vacancy rate" value={pct(demand.vacancy_rate, 2)} testId="stat-vacancy" />
                <Stat label="Days on market" value={typeof demand.dom === "number" ? `${Math.round(demand.dom)} days` : "—"} />
                <Stat label="Vendor discounting" value={pct(demand.discounting)} />
                <Stat label="Auction clearance" value={pct(demand.clearance_rate)} />
                <Stat label="Inventory" value={typeof supply.inventory === "number" ? `${supply.inventory.toFixed(1)} mths` : "—"} />
                <Stat label="Stock on market" value={pct(supply.som_percent, 2)} />
                <Stat label="Building approvals" value={typeof supply.building_approvals_estimated === "number" ? Math.round(supply.building_approvals_estimated) : "—"} />
                <Stat label="Typical hold period" value={typeof supply.hold_period === "number" ? `${supply.hold_period.toFixed(1)} yrs` : "—"} />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Stat label="Buyer demand (search index)" value={typeof demand.buy_si === "number" ? `${demand.buy_si}/5` : "—"} />
                <Stat label="Renter demand (search index)" value={typeof demand.rent_si === "number" ? `${demand.rent_si}/5` : "—"} />
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-slate-400 text-center">
            General information only — not financial advice.
          </p>
        </div>
      )}
    </div>
  );
}
